import React, { createContext, ReactNode, useReducer, useContext } from 'react';

import { IEventEnrollment } from '../../../../../store/admin/eventEnrollments/store';
import { IAdminFlight } from '../../../../../store/admin/eventFlights/store';

import { IObjectById } from '../../../../../contrib/Utils/getObjectByIdFromArray';
import { ITeam } from '../../../../../store/admin/eventTeams/store';
import { GlobalContext } from '../../../../../store';

type IActions =
  | { type: 'cancelEditMode' }
  | { type: 'closeTeamPlayerSelectorModal' }
  | { type: 'openTeamPlayerSelectorModal' }
  | { type: 'resetEditModeEnrollmentId' }
  | { type: 'resetNewTeam' }
  | { type: 'resetPlayerToSwap' }
  | { type: 'resetTeamToAdd' }
  | { type: 'selectTeam'; value: ITeam }
  | { type: 'setCourseHandicap'; value: number | undefined }
  | { type: 'setDisplayNewTeam'; value: boolean }
  | { type: 'setEditModeEnrollmentId'; value: number }
  | { type: 'setEnrollments'; value: IObjectById<IEventEnrollment> }
  | { type: 'setEventFlights'; value: IObjectById<IAdminFlight> }
  | { type: 'setPlayerToSwap'; value: number }
  | { type: 'setSelectedTeamEnrollments'; value: number[] }
  | { type: 'setTeamToAdd'; value: ITeam }
  | { type: 'toggleDisplayNewTeam' }
  | { type: 'updateSelectedTeamEnrollments'; value: number[] };

interface ITeamsCreatorState {
  courseHandicap?: number;
  displayNewTeam: boolean;
  displayTeamPlayerSelectorModal: boolean;
  editMode: boolean;
  editModeEnrollmentId: number;
  enrollments: IObjectById<IEventEnrollment>;
  flights: IObjectById<IAdminFlight>;
  newTeam: ITeam;
  playerToSwap: number;
  selectedTeam?: ITeam;
  teamToAdd: ITeam | null;
}

const newTeam: ITeam = {
  customName: '',
  enrollments: [],
  event: -1,
  flight: -1,
  handicap: '0',
  id: -1,
  isValid: false,
  order: -1
};

const initialState: ITeamsCreatorState = {
  displayNewTeam: false,
  displayTeamPlayerSelectorModal: false,
  editMode: false,
  editModeEnrollmentId: -1,
  enrollments: {},
  flights: {},
  newTeam,
  playerToSwap: -1,
  teamToAdd: null
};

interface IDispatch {
  dispatch: (action: IActions) => void;
  actions: IBoundActionCreators;
}

const TeamsCreatorStateContext = createContext<ITeamsCreatorState | undefined>(undefined);
const TeamsCreatorDispatchContext = createContext<IDispatch | undefined>(undefined);

const TeamsCreatorReducer = (state: ITeamsCreatorState, action: IActions): ITeamsCreatorState => {
  switch (action.type) {
    case 'toggleDisplayNewTeam': {
      return {
        ...state,
        displayNewTeam: !state.displayNewTeam
      };
    }
    case 'setDisplayNewTeam': {
      return {
        ...state,
        displayNewTeam: action.value
      };
    }
    case 'setEnrollments': {
      return {
        ...state,
        enrollments: action.value
      };
    }
    case 'setCourseHandicap': {
      return {
        ...state,
        courseHandicap: action.value
      };
    }
    case 'setEventFlights': {
      return {
        ...state,
        flights: action.value
      };
    }
    case 'setEditModeEnrollmentId': {
      return {
        ...state,
        editModeEnrollmentId: action.value
      };
    }
    case 'resetEditModeEnrollmentId': {
      return {
        ...state,
        editModeEnrollmentId: -1
      };
    }
    case 'resetNewTeam': {
      return {
        ...state,
        newTeam
      };
    }
    case 'setTeamToAdd': {
      return {
        ...state,
        teamToAdd: action.value
      };
    }
    case 'resetTeamToAdd': {
      return {
        ...state,
        teamToAdd: null
      };
    }
    case 'setPlayerToSwap': {
      return {
        ...state,
        playerToSwap: action.value
      };
    }
    case 'resetPlayerToSwap': {
      return {
        ...state,
        playerToSwap: -1
      };
    }
    case 'selectTeam': {
      return {
        ...state,
        editMode: true,
        selectedTeam: action.value
      };
    }
    case 'cancelEditMode': {
      return {
        ...state,
        editMode: false
      };
    }
    case 'openTeamPlayerSelectorModal': {
      return { ...state, displayTeamPlayerSelectorModal: true };
    }
    case 'closeTeamPlayerSelectorModal': {
      return { ...state, displayTeamPlayerSelectorModal: false };
    }
    case 'setSelectedTeamEnrollments': {
      return {
        ...state,
        selectedTeam: state.selectedTeam
          ? {
              ...state.selectedTeam,
              enrollments: action.value
            }
          : state.selectedTeam
      };
    }
    case 'updateSelectedTeamEnrollments': {
      const selectedTeam = state.selectedTeam
        ? { ...state.selectedTeam, enrollments: action.value }
        : undefined;
      return selectedTeam
        ? {
            ...state,
            selectedTeam
          }
        : state;
    }
  }
};

interface TeamsProviderProps {
  children: ReactNode;
}

interface IBoundActionCreators {
  [id: string]: any;
}

export type IStateDispatchActions<T> = (
  state: ITeamsCreatorState,
  dispatch: (action: IActions) => void
) => T;

const TeamsCreatorProvider = ({ children }: TeamsProviderProps) => {
  const [state, dispatch] = useReducer(TeamsCreatorReducer, initialState as never);

  const {} = useContext(GlobalContext);

  const actions: IBoundActionCreators = {};

  return (
    <TeamsCreatorStateContext.Provider value={state}>
      <TeamsCreatorDispatchContext.Provider value={{ dispatch, actions }}>
        {children}
      </TeamsCreatorDispatchContext.Provider>
    </TeamsCreatorStateContext.Provider>
  );
};

const useTeamsCreatorState = () => {
  const context = React.useContext(TeamsCreatorStateContext);
  if (context === undefined) {
    throw new Error('useTeamsCreatorState must be used within a TeamsCreatorProvider');
  }
  return context;
};

const useTeamsCreatorDispatch = () => {
  const context = React.useContext(TeamsCreatorDispatchContext);
  if (context === undefined) {
    throw new Error('useTeamsCreatorDispatch must be used within a TeamsCreatorProvider');
  }
  return context;
};

export { TeamsCreatorProvider, useTeamsCreatorState, useTeamsCreatorDispatch };
